import { LockIcon } from "@primer/octicons-react";
import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "../lib/utils";
import { Badge } from "./ui/badge";

/** A side menu beside the page content; below 900px the menu becomes a scrolling row above it. */
export function SideNavLayout({ nav, children }: { nav: ReactNode; children: ReactNode }) {
  return (
    <div className="mt-6 grid grid-cols-[240px_minmax(0,1fr)] items-start gap-6 max-md:grid-cols-[minmax(0,1fr)]">
      {nav}
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** Keeps the selected entry in view when the menu scrolls horizontally on narrow screens. */
export function SideNav({ label, children }: { label: string; children: ReactNode }) {
  const { pathname } = useLocation();
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    ref.current?.querySelector('[aria-current="page"]')?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [pathname]);
  return (
    <nav
      className="sticky top-4 flex flex-col gap-0.5 max-md:static max-md:flex-row max-md:overflow-x-auto max-md:border-b max-md:border-border-muted max-md:pb-2"
      aria-label={label}
      ref={ref}
    >
      {children}
    </nav>
  );
}

export function SideNavHead({ icon, title, subtitle }: { icon: ReactNode; title: string; subtitle?: ReactNode }) {
  return (
    <div className="mb-2 flex items-center gap-2 border-b border-border-muted px-2 pb-3 max-md:hidden">
      <span className="grid size-8 shrink-0 place-items-center rounded-md border bg-muted text-muted-foreground">{icon}</span>
      <div className="flex min-w-0 flex-col">
        <b className="truncate">{title}</b>
        {subtitle && <span className="truncate text-xs text-muted-foreground">{subtitle}</span>}
      </div>
    </div>
  );
}

export function SideNavGroup({ children }: { children: ReactNode }) {
  return <div className="mt-2 border-t border-border-muted px-2 pb-1.5 pt-4 text-xs font-semibold text-muted-foreground max-md:hidden">{children}</div>;
}

/** Selected entries carry aria-current="page": NavLink sets it, plain links pass `current`. */
export const sideNavItem = cn(
  "relative flex items-center gap-2 whitespace-nowrap rounded-md px-2 py-1.5 text-foreground hover:bg-neutral-muted hover:no-underline md:whitespace-normal",
  "[&_svg]:shrink-0 [&_svg:not([class*=text-])]:text-muted-foreground",
  "aria-[current=page]:bg-neutral-muted aria-[current=page]:font-semibold aria-[current=page]:[&_svg:not([class*=text-])]:text-foreground",
  "md:aria-[current=page]:before:absolute md:aria-[current=page]:before:-left-2 md:aria-[current=page]:before:inset-y-1.5 md:aria-[current=page]:before:w-1 md:aria-[current=page]:before:rounded-md md:aria-[current=page]:before:bg-info-emphasis",
);

/** Set by a section whose pages the viewer may only look at; every Subhead inside it then shows a Read-only label. */
export const ReadOnlyContext = createContext(false);

export function Subhead({ title, children, actions }: { title: ReactNode; children?: ReactNode; actions?: ReactNode }) {
  const readOnly = useContext(ReadOnlyContext);
  return (
    // The block after a page header starts right under it, whatever its own top margin (a Card's mt-4).
    <div className="mb-4 flex flex-wrap items-start gap-4 [&+*]:mt-0!">
      <div className="min-w-0 flex-1">
        <h2 className="flex items-center gap-2 text-2xl font-normal">
          {title}
          {readOnly && (
            <Badge title="Only site admins and admins of this repository can change settings.">
              <LockIcon size={12} /> Read-only
            </Badge>
          )}
        </h2>
        {children && <p className="mt-1 text-muted-foreground">{children}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
